import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { format, parseISO } from "date-fns";
import { nn } from "date-fns/locale";
import { ArrowRight, CalendarClock, ChevronRight } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { SectionHeading } from "@/components/admin/PageHeader";
import { Segmented } from "@/components/admin/Toolbar";
import { StatusPill } from "@/components/admin/StatusPill";
import { WHOLE } from "@/components/admin/calendar/engine";
import { fmtRange, fmtTime } from "@/lib/format";
import { cn } from "@/lib/utils";

const LIMIT = 8;
const openLink = (e) => `/admin/kalender?view=list&omfang=alle&rediger=${e.id}`;

const DateStub = ({ iso }) => {
  const d = parseISO(iso);
  const thisYear = d.getFullYear() === new Date().getFullYear();
  return (
    <span className="w-12 shrink-0 text-center leading-none">
      <span className="block font-display text-[20px] font-semibold tabular tracking-[-0.02em]">{format(d, "d")}</span>
      <span className="t-mono-sm mt-1 block text-muted-foreground">{format(d, "MMM", { locale: nn }).replace(".", "")}{thisYear ? "" : ` ’${format(d, "yy")}`}</span>
    </span>
  );
};

const Row = ({ e, roomName, colours, past }) => (
  <li>
    <Link to={openLink(e)} className={cn("group -mx-2 flex items-center gap-3 rounded-xl px-2 py-2.5 transition-colors hover:bg-secondary/50 sm:gap-4", past && "opacity-70 hover:opacity-100")} data-testid={`terminliste-row-${e.id}`}>
      <DateStub iso={e.start_date} />
      <span className="hairline h-8 w-px shrink-0 border-l" aria-hidden="true" />
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13.5px] font-medium">{e.title}</span>
        <span className="block truncate text-[12px] text-muted-foreground">{[e.end_date && e.end_date !== e.start_date ? fmtRange(e.start_date, e.end_date) : fmtTime(e.start_time, e.end_time), e.category && e.category !== e.title ? e.category : null, e.organizer || e.contact_name].filter(Boolean).join(" · ")}</span>
      </span>
      <span className="hidden shrink-0 items-center gap-1.5 text-[12px] text-muted-foreground sm:inline-flex"><span className="h-2 w-2 rounded-full" style={{ background: `hsl(var(--room-${e.room_ids?.includes(WHOLE) ? 8 : colours[e.room_ids?.[0]] || 7}))` }} />{e.room_ids?.map(roomName).join(", ")}</span>
      <StatusPill status={e.visibility === "public" ? "public" : "privat"} className="hidden md:inline-flex" />
      <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground/60 transition-transform group-hover:translate-x-0.5" aria-hidden="true" />
    </Link>
  </li>
);

const Empty = ({ icon: Icon = CalendarClock, title, text, action }) => (
  <div className="flex items-start gap-3 py-6" data-testid="terminliste-empty">
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-secondary text-muted-foreground"><Icon className="h-4 w-4" strokeWidth={1.75} /></span>
    <div className="min-w-0"><p className="text-[13.5px] font-medium">{title}</p>{text && <p className="mt-0.5 text-[12.5px] text-muted-foreground">{text}</p>}{action && <div className="mt-2">{action}</div>}</div>
  </div>
);

/** The board's terminliste on the front page – what is coming, what is reserved for later years, and what has been. */
export const TerminlistePanel = ({ upcoming = [], past = [], tbd = [], roomName, colours, loading }) => {
  const [chosen, setChosen] = useState(null);
  const tab = chosen || (upcoming.length ? "upcoming" : tbd.length ? "reserved" : "past");
  const byYear = useMemo(() => Object.entries(tbd.reduce((acc, e) => { (acc[e.year || "?"] ||= []).push(e); return acc; }, {})).sort(([a], [b]) => String(a).localeCompare(String(b))), [tbd]);
  const total = upcoming.length + past.length + tbd.length;
  const rows = tab === "upcoming" ? upcoming : tab === "past" ? past : [];
  const more = rows.length > LIMIT ? rows.length - LIMIT : 0;

  return (
    <section data-testid="dashboard-terminliste">
      <SectionHeading
        title="Terminlista"
        description={loading ? "Hentar…" : `${total} oppføringar · ${upcoming.length} komande · ${tbd.length} reserverte utan dato`}
        action={<Link to="/admin/kalender?view=list&omfang=alle" className="arrow-nudge-x inline-flex items-center gap-1 text-[13px] font-medium u-link" data-testid="dashboard-terminliste-link">Heile lista <ArrowRight className="h-3.5 w-3.5" /></Link>}
      />
      <Segmented size="sm" value={tab} onChange={setChosen} testId="dashboard-terminliste-tabs" items={[
        { value: "upcoming", label: "Komande", count: upcoming.length, testId: "dashboard-terminliste-tab-upcoming" },
        { value: "reserved", label: "Reservert", count: tbd.length, testId: "dashboard-terminliste-tab-reserved" },
        { value: "past", label: "Historiske", count: past.length, testId: "dashboard-terminliste-tab-past" },
      ]} />
      <div className="mt-2" data-testid={`dashboard-terminliste-${tab}`}>
        {loading ? <div className="space-y-2 py-2">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-11 rounded-xl" />)}</div> : tab === "reserved" ? (
          byYear.length === 0 ? <Empty title="Ingen reservasjonar utan dato" text="Kryss av for «Dato ikkje fastsett» i ei oppføring for å halde av eit år." /> : (
            <ol className="divide-y divide-hairline">
              {byYear.map(([year, list]) => (
                <li key={year} className="flex items-start gap-3 py-3 sm:gap-4" data-testid={`terminliste-year-${year}`}>
                  <span className="w-12 shrink-0 font-display text-[20px] font-semibold leading-none tabular tracking-[-0.02em]">{year}</span>
                  <span className="hairline mt-0.5 h-5 w-px shrink-0 border-l" aria-hidden="true" />
                  <div className="flex min-w-0 flex-1 flex-wrap gap-1.5">
                    {list.map((e) => (
                      <Link key={e.id} to={`/admin/kalender?view=list&fane=tbd&rediger=${e.id}`} className="inline-flex max-w-full items-center gap-1.5 rounded-full bg-secondary/80 px-2.5 py-1 text-[12px] transition-colors hover:bg-secondary" data-testid={`terminliste-tbd-${e.id}`}>
                        <span className="truncate">{e.title} · {e.room_ids?.map(roomName).join(", ")}</span>{e.contact_name && <span className="hidden truncate text-muted-foreground sm:inline">– {e.contact_name}</span>}
                      </Link>
                    ))}
                  </div>
                  <span className="t-mono-sm hidden shrink-0 pt-1 text-muted-foreground sm:block">{list.length} {list.length === 1 ? "reservasjon" : "reservasjonar"}</span>
                </li>
              ))}
            </ol>
          )
        ) : rows.length === 0 ? (
          tab === "upcoming"
            ? <Empty title="Ingen komande oppføringar med fastsett dato" text={tbd.length ? `${tbd.length} reservasjonar ventar på dato for komande år.` : "Legg inn det neste som skjer i huset."} action={tbd.length ? <button type="button" onClick={() => setChosen("reserved")} className="text-[13px] font-medium u-link" data-testid="terminliste-show-reserved">Sjå reservasjonane</button> : null} />
            : <Empty title="Ingen historiske oppføringar enno" />
        ) : (
          <>
            <ol className="divide-y divide-hairline">{rows.slice(0, LIMIT).map((e) => <Row key={e.id} e={e} roomName={roomName} colours={colours} past={tab === "past"} />)}</ol>
            {more > 0 && <Link to={`/admin/kalender?view=list&omfang=${tab === "past" ? "historiske" : "komande"}`} className="mt-2 inline-flex items-center gap-1 text-[13px] font-medium text-muted-foreground hover:text-foreground" data-testid="dashboard-terminliste-more">+{more} til i terminlista <ArrowRight className="h-3.5 w-3.5" /></Link>}
          </>
        )}
      </div>
    </section>
  );
};
